import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Button, Space, message, Alert, Typography, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { apiFetch } from '../api';

const { Paragraph, Text } = Typography;

const Settings: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; detail: string } | null>(null);
  const [ttTesting, setTtTesting] = useState(false);
  const [ttResult, setTtResult] = useState<any | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [logoSaving, setLogoSaving] = useState(false);
  const [pwForm] = Form.useForm();
  const [pwSaving, setPwSaving] = useState(false);

  const handleChangePassword = async (values: any) => {
    setPwSaving(true);
    try {
      const res = await apiFetch<{ ok: boolean; detail: string; access_token?: string }>('/auth/change-password', {
        method: 'POST',
        body: { old_password: values.old_password, new_password: values.new_password },
      });
      // Сервер выдаёт свежий токен — текущую сессию не разлогинит.
      if (res.access_token) localStorage.setItem('token', res.access_token);
      message.success(res.detail || 'Пароль изменён');
      pwForm.resetFields();
    } catch (e: any) {
      message.error(e.message || 'Не удалось сменить пароль');
    }
    setPwSaving(false);
  };

  const saveLogo = async (dataUrl: string) => {
    setLogoSaving(true);
    try {
      await apiFetch('/settings/', { method: 'PUT', body: { logo_url: dataUrl } });
      setLogoUrl(dataUrl);
      // Живое обновление логотипа в боковом меню без перезагрузки страницы.
      window.dispatchEvent(new CustomEvent('logo-updated', { detail: dataUrl }));
      message.success(dataUrl ? 'Логотип сохранён' : 'Логотип удалён');
    } catch (e: any) {
      message.error(e.message || 'Не удалось сохранить логотип');
    }
    setLogoSaving(false);
  };

  const onLogoSelected = (file: File) => {
    if (file.size > 512 * 1024) {
      message.error('Логотип слишком большой (макс. 512 КБ)');
      return false;
    }
    const reader = new FileReader();
    reader.onload = () => saveLogo(String(reader.result));
    reader.readAsDataURL(file);
    return false; // не отправлять через antd Upload — сохраняем сами
  };

  const handleTikTokTest = async (values: any) => {
    setTtTesting(true);
    setTtResult(null);
    try {
      const res = await apiFetch<any>('/settings/test-tiktok', {
        method: 'POST',
        body: { access_token: values.access_token, video_id: values.video_id },
      });
      setTtResult(res);
    } catch (e: any) {
      message.error(e.message || 'Ошибка проверки TikTok');
    }
    setTtTesting(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiFetch<{ tiktok_proxy?: string; logo_url?: string }>('/settings/');
      form.setFieldsValue({ tiktok_proxy: data.tiktok_proxy || '' });
      setLogoUrl(data.logo_url || '');
    } catch (e: any) {
      message.error(e.message || 'Не удалось загрузить настройки');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      await apiFetch('/settings/', { method: 'PUT', body: { tiktok_proxy: values.tiktok_proxy || '' } });
      message.success('Настройки сохранены');
    } catch (e: any) {
      message.error(e.message || 'Не удалось сохранить');
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const proxy = form.getFieldValue('tiktok_proxy');
      const res = await apiFetch<{ ok: boolean; detail: string; suggested_proxy?: string }>('/settings/test-proxy', {
        method: 'POST',
        body: { proxy },
      });
      setTestResult(res);
      // Если бэкенд подсказал корректную схему (http:// для HTTP-прокси) — подставим её.
      if (res.suggested_proxy) {
        form.setFieldsValue({ tiktok_proxy: res.suggested_proxy });
      }
    } catch (e: any) {
      setTestResult({ ok: false, detail: e.message || 'Ошибка проверки' });
    }
    setTesting(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 720 }}>
    <Card title="Смена пароля">
      <Form form={pwForm} layout="vertical" onFinish={handleChangePassword} style={{ maxWidth: 360 }}>
        <Form.Item
          name="old_password"
          label="Текущий пароль"
          rules={[{ required: true, message: 'Введите текущий пароль' }]}
        >
          <Input.Password autoComplete="current-password" placeholder="Текущий пароль" />
        </Form.Item>
        <Form.Item
          name="new_password"
          label="Новый пароль"
          rules={[{ required: true, message: 'Введите новый пароль' }, { min: 6, message: 'Минимум 6 символов' }]}
        >
          <Input.Password autoComplete="new-password" placeholder="Новый пароль" />
        </Form.Item>
        <Form.Item
          name="confirm"
          label="Повторите новый пароль"
          dependencies={['new_password']}
          rules={[
            { required: true, message: 'Повторите новый пароль' },
            ({ getFieldValue }) => ({
              validator: (_, value) =>
                !value || getFieldValue('new_password') === value
                  ? Promise.resolve()
                  : Promise.reject(new Error('Пароли не совпадают')),
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" placeholder="Повторите новый пароль" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={pwSaving}>Сменить пароль</Button>
      </Form>
    </Card>
    <Card title="Настройки" loading={loading}>
      <div style={{ marginBottom: 24, borderBottom: '1px solid #f0f0f0', paddingBottom: 16 }}>
        <Text strong>Логотип (в боковом меню)</Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
          <div style={{ width: 160, height: 56, background: '#001529', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            {logoUrl ? (
              <img src={logoUrl} alt="logo" style={{ maxWidth: '100%', maxHeight: 48, objectFit: 'contain' }} />
            ) : (
              <span style={{ color: '#8c8c8c', fontSize: 12 }}>нет логотипа</span>
            )}
          </div>
          <Space>
            <Upload accept="image/*" showUploadList={false} beforeUpload={onLogoSelected}>
              <Button icon={<UploadOutlined />} loading={logoSaving}>Загрузить</Button>
            </Upload>
            {logoUrl && <Button danger onClick={() => saveLogo('')} loading={logoSaving}>Удалить</Button>}
          </Space>
        </div>
        <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
          PNG/SVG/JPG до 512 КБ. Сразу отобразится вверху бокового меню.
        </Paragraph>
      </div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        title="Прокси для TikTok"
        description="TikTok API недоступен напрямую из Казахстана — запросы к TikTok идут через указанный прокси. Instagram работает напрямую, прокси для него не используется."
      />
      <Form form={form} layout="vertical" onFinish={handleSave}>
        <Form.Item
          name="tiktok_proxy"
          label="Прокси-сервер для TikTok"
          tooltip="Формат: http://user:pass@host:port или socks5://host:port"
        >
          <Input placeholder="http://user:pass@host:port" allowClear />
        </Form.Item>
        <Paragraph type="secondary" style={{ marginTop: -8 }}>
          Поддерживаются <Text code>http://</Text>, <Text code>https://</Text>, <Text code>socks5://</Text>.
          Пусто — TikTok без прокси.
        </Paragraph>
        <Space>
          <Button type="primary" htmlType="submit" loading={saving}>Сохранить</Button>
          <Button onClick={handleTest} loading={testing}>Проверить прокси</Button>
        </Space>
      </Form>
      {testResult && (
        <Alert
          style={{ marginTop: 16 }}
          type={testResult.ok ? 'success' : 'error'}
          showIcon
          title={testResult.detail}
        />
      )}

      <div style={{ marginTop: 24, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          title="Проверка доступа к TikTok"
          description="Шлёт тестовый запрос к TikTok Research API через прокси и напрямую. Даже ответ TikTok с ошибкой авторизации означает, что запрос дошёл (egress работает). Токен можно не указывать."
        />
        <Form layout="inline" onFinish={handleTikTokTest}>
          <Form.Item name="video_id">
            <Input placeholder="video_id (опц.)" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="access_token">
            <Input placeholder="TikTok токен (опц.)" style={{ width: 240 }} />
          </Form.Item>
          <Form.Item>
            <Button onClick={() => {}} htmlType="submit" loading={ttTesting}>Проверить TikTok</Button>
          </Form.Item>
        </Form>
        {ttResult && (
          <div style={{ marginTop: 12 }}>
            <Alert
              type={ttResult.via_proxy?.reached ? 'success' : 'error'}
              showIcon
              style={{ marginBottom: 8 }}
              title={`Через прокси: ${ttResult.via_proxy?.reached ? `дошёл (HTTP ${ttResult.via_proxy.status})` : `не дошёл — ${ttResult.via_proxy?.error || 'нет прокси'}`}`}
              description={ttResult.via_proxy?.body ? <Text code style={{ whiteSpace: 'pre-wrap' }}>{ttResult.via_proxy.body}</Text> : undefined}
            />
            <Alert
              type={ttResult.direct?.reached ? 'success' : 'warning'}
              showIcon
              title={`Напрямую: ${ttResult.direct?.reached ? `дошёл (HTTP ${ttResult.direct.status})` : `не дошёл — ${ttResult.direct?.error || ''}`}`}
            />
          </div>
        )}
      </div>
    </Card>
    </div>
  );
};

export default Settings;
